import { AlertCircle, Loader2 } from 'lucide-react';
import { avatarColor, initials } from '@mona-roadmap/domain';
import type { PersonRef } from '@mona-roadmap/domain';
import type { CSSProperties } from 'react';
import type { FieldState } from '../../lib/fieldState';

const colorVar = (hex: string | undefined): CSSProperties => ({ '--c': hex ?? '#9aa3b2' }) as CSSProperties;

/** monday status colours are desaturated into the dark UI: tinted background, soft border, readable text. */
export function StatusChip({ label, color }: { label: string; color?: string }) {
  return (
    <span className="chip" style={colorVar(color)} title={label}>
      {label}
    </span>
  );
}

/** Compact dot with an accessible label (used for Scoping Status). */
export function StatusDot({ label, color, field }: { label: string | null; color?: string; field: string }) {
  if (!label) return <span className="text-faint" aria-label={`${field}: none`} title={`${field}: none`}>-</span>;
  return (
    <span role="img" aria-label={`${field}: ${label}`} title={label} className="inline-flex">
      <span className="dot" style={colorVar(color)} />
    </span>
  );
}

export function SprintPill({ label, colors }: { label: string; colors?: { bg: string; fg: string } }) {
  const style: CSSProperties = colors ? { background: colors.bg, color: colors.fg } : { background: 'var(--line-strong)', color: 'var(--ink)' };
  return (
    <span className="pill" style={style} title={label}>
      {label}
    </span>
  );
}

export function Avatar({ person }: { person: PersonRef }) {
  const c = avatarColor(person.id);
  return (
    <span className="avatar" style={{ background: c.bg, color: c.fg }} title={person.name} aria-hidden>
      {initials(person.name)}
    </span>
  );
}

/** Overlapped avatar stack; the accessible name lists every person. */
export function AvatarStack({ people, max = 3 }: { people: PersonRef[]; max?: number }) {
  if (people.length === 0) return <span className="text-faint">-</span>;
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className="inline-flex items-center" role="img" aria-label={people.map((p) => p.name).join(', ')} title={people.map((p) => p.name).join(', ')}>
      {shown.map((p) => (
        <Avatar key={p.id} person={p} />
      ))}
      {extra > 0 ? (
        <span className="avatar -ml-1.5" style={{ background: 'var(--line-strong)', color: 'var(--ink)' }}>
          +{extra}
        </span>
      ) : null}
    </span>
  );
}

export function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <span className="bar" data-complete={total > 0 && done === total} role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label={`${done} of ${total} done`}>
      <i style={{ width: `${pct}%` }} />
    </span>
  );
}

/** Spinner / error marker shown next to a field while it saves or after it failed. */
export function FieldStatusIcon({ state }: { state: FieldState | undefined }) {
  if (state?.status === 'saving') return <Loader2 className="size-3 flex-none animate-spin text-faint" aria-label="Saving" role="status" />;
  if (state?.status === 'error')
    return (
      <span title={state.message} role="img" aria-label={`Save failed: ${state.message ?? ''}`} className="flex-none text-danger">
        <AlertCircle className="size-3.5" />
      </span>
    );
  return null;
}
